export function getMatchedTags(product, selectedTags) {
  if (!selectedTags?.length) return [];

  return selectedTags.filter((tag) =>
    product.tags?.some((productTag) => productTag.id === tag.id)
  );
}

export function getTagMatchCount(product, selectedTags) {
  if (!selectedTags?.length) return 0;

  return getMatchedTags(product, selectedTags).length;
}

export function rankProductsByTags(products, selectedTags) {
  if (!selectedTags?.length) return products || [];

  return buildProductTagSections(products, selectedTags).flatMap(
    (section) => section.products
  );
}

export function formatTagList(tags) {
  const names = (tags || []).map((tag) => tag.name).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} e ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

const SECONDARY_PROVIDER_LIMIT = 3;
const HERO_ENOUGH_SHOPS = 3;

function tagSetKey(tags) {
  return tags.map((tag) => tag.id).slice().sort((a, b) => a - b).join(',');
}

function scoreProductList(products, selectedTags) {
  return (products || [])
    .map((product) => {
      const matchedTags = getMatchedTags(product, selectedTags);
      return { product, matchedTags, hits: matchedTags.length };
    })
    .filter((item) => item.hits > 0);
}

function scoredProducts(provider, selectedTags) {
  return scoreProductList(provider.products, selectedTags);
}

export function buildProductTagSections(products, selectedTags) {
  if (!selectedTags?.length || !products?.length) return [];

  const scored = scoreProductList(products, selectedTags);
  if (!scored.length) return [];

  if (selectedTags.length === 1) {
    return [{
      key: `single-query-${selectedTags[0].id}`,
      hits: 1,
      matchedTags: selectedTags,
      missingTags: [],
      isHero: true,
      isSingle: false,
      products: scored.map((item) => item.product),
    }];
  }

  const maxHits = Math.max(0, ...scored.map((item) => item.hits));
  const usedProductIds = new Set();
  const sections = [];

  for (let hits = maxHits; hits >= 2; hits -= 1) {
    const setMap = new Map();

    scored.forEach((item) => {
      if (usedProductIds.has(item.product.id) || item.hits !== hits) return;

      const key = tagSetKey(item.matchedTags);
      if (!setMap.has(key)) {
        setMap.set(key, { matchedTags: item.matchedTags, hits, products: [] });
      }
      setMap.get(key).products.push(item.product);
    });

    const isHero = hits === maxHits;

    [...setMap.values()]
      .sort((a, b) => b.products.length - a.products.length)
      .forEach((group) => {
        group.products.forEach((product) => usedProductIds.add(product.id));
        sections.push({
          key: tagSetKey(group.matchedTags),
          hits: group.hits,
          matchedTags: group.matchedTags,
          missingTags: selectedTags.filter(
            (tag) => !group.matchedTags.some((matched) => matched.id === tag.id)
          ),
          isHero,
          isSingle: false,
          products: group.products,
        });
      });
  }

  if (maxHits >= 2) return sections;

  selectedTags.forEach((tag) => {
    const leftover = scored.filter(
      (item) =>
        !usedProductIds.has(item.product.id) &&
        item.hits === 1 &&
        item.matchedTags[0]?.id === tag.id
    );
    if (!leftover.length) return;

    leftover.forEach((item) => usedProductIds.add(item.product.id));
    sections.push({
      key: `single-${tag.id}`,
      hits: 1,
      matchedTags: [tag],
      missingTags: selectedTags.filter((selected) => selected.id !== tag.id),
      isHero: true,
      isSingle: true,
      products: leftover.map((item) => item.product),
    });
  });

  return sections;
}

function pickBestSet(items) {
  const bySet = new Map();

  items.forEach((item) => {
    const key = tagSetKey(item.matchedTags);
    if (!bySet.has(key)) {
      bySet.set(key, { matchedTags: item.matchedTags, items: [] });
    }
    bySet.get(key).items.push(item);
  });

  return [...bySet.values()].sort((a, b) => b.items.length - a.items.length)[0];
}

function toProviderResult(entry) {
  return {
    ...entry.provider,
    products: entry.products,
  };
}

export function buildSearchResultSections(providers, selectedTags) {
  if (!selectedTags?.length || !providers?.length) return [];

  const providerEntries = providers
    .map((provider) => ({ provider, products: scoredProducts(provider, selectedTags) }))
    .filter((entry) => entry.products.length > 0);

  if (!providerEntries.length) return [];

  if (selectedTags.length === 1) {
    return [{
      key: `single-query-${selectedTags[0].id}`,
      hits: 1,
      matchedTags: selectedTags,
      missingTags: [],
      isHero: true,
      isSingle: false,
      providers: providerEntries.map((entry) => ({
        ...entry.provider,
        products: entry.products.map((item) => item.product),
      })),
    }];
  }

  const maxHits = Math.max(
    0,
    ...providerEntries.flatMap((entry) => entry.products.map((item) => item.hits))
  );
  const usedProviderIds = new Set();
  const sections = [];

  for (let hits = maxHits; hits >= 2; hits -= 1) {
    const setMap = new Map();

    providerEntries.forEach((entry) => {
      if (usedProviderIds.has(entry.provider.id)) return;

      const matching = entry.products.filter((item) => item.hits === hits);
      if (!matching.length) return;

      const best = pickBestSet(matching);
      const key = tagSetKey(best.matchedTags);
      if (!setMap.has(key)) {
        setMap.set(key, { matchedTags: best.matchedTags, hits, providers: [] });
      }
      setMap.get(key).providers.push({
        provider: entry.provider,
        products: best.items.map((item) => item.product),
      });
    });

    const isHero = hits === maxHits;
    const limit = isHero ? Number.POSITIVE_INFINITY : SECONDARY_PROVIDER_LIMIT;

    [...setMap.values()]
      .sort((a, b) => b.providers.length - a.providers.length)
      .forEach((group) => {
        const sliced = group.providers.slice(0, limit);
        sliced.forEach((entry) => usedProviderIds.add(entry.provider.id));
        sections.push({
          key: tagSetKey(group.matchedTags),
          hits: group.hits,
          matchedTags: group.matchedTags,
          missingTags: selectedTags.filter(
            (tag) => !group.matchedTags.some((matched) => matched.id === tag.id)
          ),
          isHero,
          isSingle: false,
          providers: sliced.map(toProviderResult),
        });
      });
  }

  selectedTags.forEach((tag) => {
    const leftover = [];

    providerEntries.forEach((entry) => {
      if (usedProviderIds.has(entry.provider.id)) return;

      const matching = entry.products.filter(
        (item) => item.hits === 1 && item.matchedTags[0]?.id === tag.id
      );
      if (!matching.length) return;

      leftover.push({
        provider: entry.provider,
        products: matching.map((item) => item.product),
      });
    });

    if (!leftover.length) return;

    const isHero = maxHits === 1;
    const sliced = leftover.slice(0, isHero ? leftover.length : SECONDARY_PROVIDER_LIMIT);
    sliced.forEach((entry) => usedProviderIds.add(entry.provider.id));

    sections.push({
      key: `single-${tag.id}`,
      hits: 1,
      matchedTags: [tag],
      missingTags: selectedTags.filter((selected) => selected.id !== tag.id),
      isHero,
      isSingle: true,
      providers: sliced.map(toProviderResult),
    });
  });

  const heroShopCount = sections
    .filter((section) => section.isHero)
    .reduce((count, section) => count + section.providers.length, 0);

  if (heroShopCount >= HERO_ENOUGH_SHOPS) {
    return sections.filter((section) => section.isHero);
  }

  return sections;
}

export function searchSectionTitle() {
  return 'Resultados para:';
}
